import { UserManager } from "services/util/UserManager";
import { ColumnInfoType } from "store/project/data/useTableDataStore";
import {
  AdminListType,
  ColumnNameType,
  ColumnSortType,
  UserInfoKeyType,
  ValidOptionType,
} from "types/admin/AdminType";
import {
  ColumnDataType,
  DropDownListOptionStringType,
  DropDownListOptionType,
  NonNullable,
  ObjectDifference,
} from "types/common/CommonType";

import {
  ConnectorDbValueType,
  DatasourceListType,
} from "types/connectorAndDatasource/ConnectorAndDatasourceType";
import { DEV_MODE } from "config";
import {
  COLUMN_NAME_RESERVED_WORDS,
  DATASET_COLORSET,
  FILE_FORMAT_OPTION_INIT_DTO_EXPORT,
} from "const/const";
import { StepCommandType } from "container/Project/ProjectData/sideBar/steps";
import {
  ApfSearchParameter,
  GetProjectFlowListForm,
} from "container/ProjectFlows/APFDetailsModal/type/APFType";
import CryptoJS from "crypto-js";
import _ from "lodash";
import useErrorStore from "store/useErrorStore";
import { v4 as uuidv4 } from "uuid";
import { LibraryFileFormatKeyType } from "types/library/LibraryType";
import { IndexObjectType } from "components/dataGrid/LibraryDataGrip";
interface ObjectCloseType {
  mousedown?: boolean;
  escape?: boolean;
  wheelBlock?: boolean;
  resize?: boolean;
  click?: boolean;
}

export type ValidResponseType =
  | { isValid: false; errorMessage: string }
  | { isValid: true };
export class Util {
  /**
   *
   * @author hc_yang
   * @param res API response 데이터
   * @returns response 내 데이터 반환
   * @description API 호출 결과 유효성 검사 함수,
   *  - 유효한 호출인 경우 JSON형태의 결과 데이터 반환,
   *  - 그렇지 않을 경우 에러 메시지 or false 반환
   */
  static fetchAndSetData = async (res: Response | null | undefined) => {
    try {
      const userManager = new UserManager();
      const { setErrorCode } = useErrorStore.getState();
      if (!res) throw res;
      const data = await res.json();

      if (res.status === 200 || res.status === 201) {
        if (data.code === 401) {
          userManager.clear();
          setErrorCode(401);
        }
        return data;
        // 수정 예정
      } else if (res.status === 400 || res.status === 403) {
        /**
         * @description
         * EditDetailsPermissionsTemplateContainer handleSaveButtonClick 함수 참고
         * 모달 업데이트시에  if (data) return true;로 보고 있어
         * 업데이트 실패가 나도 true로 인식하여 모달이 닫히지 않는 문제가 발생
         * 하여 임시 주석 추가
         * @author jh_jeong
         */
        if (data.code === 403) return false;
        // if (data.code === 409) return data;
        return data;
      } else throw res;
    } catch (e) {
      return false;
    }
  };

  /**
   * @todo APF에서 에러 핸들링을 위해 잠시 함수 분리하겠습니다. 혹시 이후에 데이터 파싱하는 함수 수정되면 삭제할 예정입니다.
   * @author dorosee-jsbaek
   */
  static fetchAndSetDataWithErr = async (res: Response | null | undefined) => {
    try {
      const userManager = new UserManager();

      if (!res) throw res;

      const data = await res.json();

      if (res.status === 200 || res.status === 201) {
        if (data.code === 401) {
          userManager.clear();
          window.location.href = "/#/error/401";
        }

        if (data.isSuccess && data.code === 200) return data;
      } else if (res.status) {
        // 에러 처리를 위해 code 200 외 나머지 값에 대한 내용은 아래와 같이 정의하여 return
        return {
          code: data.code,
          message: data.message,
          isSuccess: data.isSuccess,
        };
      } else throw res;
    } catch (e) {
      return false;
    }
  };
  static getTodayYYYYMMDD = () => {
    const date = new Date();
    const formattedDate = date
      .toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/. /g, "-")
      .replace(".", "");

    return formattedDate;
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static formatDate = (data: string | number, isTime: boolean) => {
    if (!data) return "-";
    const date = new Date(data);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    if (isTime) {
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      const hourNumber = parseInt(hour, 10); // Convert hour to a number
      const ampm = hourNumber >= 12 ? "PM" : "AM";
      const formattedHour = String(hourNumber % 12 || 12).padStart(2, "0");

      return `${year}.${month}.${day} ${ampm} ${formattedHour}:${minute}`;
    } else {
      return `${year}.${month}.${day}`;
    }
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static getNewColumnName = (
    oldColumnName: string,
    columnData: ColumnDataType[]
  ): string => {
    let index = 1;
    let newColumnName = `${oldColumnName} (${index})`;
    const existingColumnNames = columnData.map((column: any) => column.name);

    while (existingColumnNames.includes(newColumnName)) {
      index += 1;
      newColumnName = `${oldColumnName} (${index})`;
    }
    return newColumnName;
  };

  /**
   * @param oldName 입력한 이름
   * @param existingNameList 기존 이름 리스트
   * @returns 중복 체크 후 변환된 이름
   * @description 기존 이름 배열을 조회해, 중복체크 후 중복된다면, 입력한 이름 뒤에 1씩 더해서 붙여주는 함수
   */
  static incrementDuplicateNames = (
    oldName: string,
    existingNameList: string[]
  ): string => {
    let index = 1;
    let newName = oldName;

    while (existingNameList.includes(newName)) {
      index += 1;
      newName = `${oldName} (${index})`;
    }
    return newName;
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static caseKebabToCamel = (kebabCase: string): string => {
    return kebabCase.includes("-")
      ? kebabCase.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
      : kebabCase;
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static handleKeyPress(event: KeyboardEvent, key: string, action: () => void) {
    if (event.key === key) {
      action();
    }
  }

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static checkValidateUser = (
    key: UserInfoKeyType,
    value: string,
    validOption?: ValidOptionType
  ) => {
    switch (key) {
      case "loginId":
        switch (validOption) {
          case "isValidLength":
            return /^.{4,}$/.test(value);
          case "isValidCharacter":
            return !/\//.test(value);
          default:
            return false;
        }
      case "password": {
        switch (validOption) {
          case "isValidLowerLetter":
            return /[?a-z]/g.test(value);
          case "isValidNumber":
            return /\d/g.test(value);
          case "isValidSpecialLetter":
            return /[^ㄱ-ㅎㅏ-ㅣ가-힣a-zA-Z0-9]/g.test(value);
          case "isValidLength":
            return /^.{8,}$/g.test(value);
          default:
            return false;
        }
      }

      case "email":
        return /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i.test(
          value
        );
      case "username":
        return /^(?=.*[a-z0-9A-Z가-힣])[a-z0-9A-Z가-힣]{2,16}$/.test(value);
      case "phoneNumber":
        return /^010-\d{4}-\d{4}$/.test(value);
      default:
        return false;
    }
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static convertDropDownList = (
    originData: ColumnInfoType[],
    labelKey: keyof ColumnInfoType,
    valueKey: keyof ColumnInfoType,
    dbTypeKey?: keyof ColumnInfoType
  ): DropDownListOptionStringType[] => {
    let dropDownList: DropDownListOptionStringType[] = [];
    for (let data of originData) {
      // if (!data["columnInfoId"]) continue;
      // if (!data.columnInfoId) continue;
      if (data.columnName === "$$_Sources") continue;

      let dropDownData: DropDownListOptionType = {
        label: "",
        value: "",
        dbType: "",
      };
      if (!dbTypeKey) delete dropDownData.dbType;

      if (data[labelKey]) dropDownData.label = `${data[labelKey]}`;
      if (data[valueKey]) dropDownData.value = `${data[valueKey]}`;
      if (dbTypeKey && data[dbTypeKey])
        dropDownData.label = `${data[dbTypeKey]}`;

      dropDownList.push(dropDownData);
    }
    return dropDownList;
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static getUniqueKey = (len?: number): string => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";

    for (let i = 0; i < (len ? len : 15); i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      key += characters.charAt(randomIndex);
    }

    return key;
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static formatPhoneNumber = (phoneNumber: string) => {
    const numericString = phoneNumber.replace(/\D/g, "");

    // 숫자가 11자리인지 확인
    if (numericString.length === 11) {
      // 정규 표현식을 사용하여 모바일 전화번호 형식으로 변환
      const formattedNumber = numericString.replace(
        /(\d{3})(\d{4})(\d{4})/,
        "$1-$2-$3"
      );
      return formattedNumber;
    } else {
      // 11자리가 아닌 경우 에러 메시지 또는 다른 처리를 수행할 수 있음
      devErrLog("입력된 전화번호의 길이가 올바르지 않습니다.");
      return phoneNumber; // 또는 다른 값을 반환
    }
  };

  /**
   * @author c_bae
   * @param fileSizeInBytes
   */
  static formatFileSize = (fileSizeInBytes: number) => {
    const units = ["Bytes", "KB", "MB", "GB"];
    if (fileSizeInBytes === 0) return "0 Byte";

    const k = 1024;
    const i = Math.floor(Math.log(fileSizeInBytes) / Math.log(k));
    const fileSize = parseFloat((fileSizeInBytes / Math.pow(k, i)).toFixed(2));

    return `${fileSize} ${units[i]}`;
  };

  /**
   * @description 첫번째 인자의 객체와, 두번째 인자의 객체를 비교하여, 서로 같은 key를 가지며, 해당 key의 값이 다를 경우 두번째 인자의 해당 key와 값을 return ( 두번째 인자의 해당 key가 falsy하면 return x )
   * @param objA {
              name: "John",
              age: 30,
              city: "New York",
              sex: "male",
              collage: "",
              hobby: "",
              country : "korea"
            },
   * @param objB {
              name: "John",
              age: 30,
              city: "Los Angeles",
              sex: "femail",
              created: "",
              hobby: "riding",
              country: ""
            }
   * @returns // { city: "Los Angeles", sex: "femail", hobby: "riding" }
   * @author c_bae
   */
  static getDifferentOnSecondObject = <T>(
    objA: T,
    objB: T
  ): ObjectDifference<T> => {
    const difference: ObjectDifference<T> = {};

    for (const key in objB) {
      if (
        Object.prototype.hasOwnProperty.call(objA, key) &&
        Object.prototype.hasOwnProperty.call(objB, key) &&
        objB[key] !== null
      ) {
        if (Array.isArray(objA[key]) && Array.isArray(objB[key])) {
          const isDifference = getArrayDifference(
            objA[key] as string[],
            objB[key] as string[]
          );

          isDifference && (difference[key] = objB[key]);
        } else if (
          typeof objA[key] === "object" &&
          typeof objB[key] === "object"
        ) {
          const nestedDifference = Util.getDifferentOnSecondObject(
            objA[key],
            objB[key]
          );

          if (Object.keys(nestedDifference).length > 0) {
            difference[key] = nestedDifference as T[keyof T];
          }
        } else {
          if (objA[key] !== objB[key] && objB[key]) {
            difference[key] = objB[key];
          }
        }
      }
    }
    return difference;
  };

  /**
   *
   * @description: getDifferentOnSecondObject 커스텀
   * @returns
   */
  static getDifferentOnSecondObjectByConnector = <T>(
    objA: T,
    objB: T
  ): ObjectDifference<T> => {
    const difference: ObjectDifference<T> = {};

    for (const key in objB) {
      if (
        Object.prototype.hasOwnProperty.call(objA, key) &&
        Object.prototype.hasOwnProperty.call(objB, key)
      ) {
        if (Array.isArray(objA[key]) && Array.isArray(objB[key])) {
          const isDifference = getArrayDifference(
            objA[key] as string[],
            objB[key] as string[]
          );

          isDifference && (difference[key] = objB[key]);
        } else if (
          typeof objA[key] === "object" &&
          typeof objB[key] === "object"
        ) {
          const nestedDifference = Util.getDifferentOnSecondObjectByConnector(
            objA[key],
            objB[key]
          );

          if (Object.keys(nestedDifference).length > 0) {
            difference[key] = nestedDifference as T[keyof T];
          }
        } else {
          // objB[key] 가 null 인경우 return 되도록
          if (objA[key] !== objB[key]) {
            if (
              [
                "queryPrefetchSize",
                "maxColumnSize",
                "exportBatchSize",
                "maxVarcharSize",
                "autoCreateTableYn",
                "countQueryYn",
              ].includes(key)
            ) {
              difference[key] = objB[key];
            } else {
              objB[key] && (difference[key] = objB[key]);
            }
          }
        }
      }
    }
    return difference;
  };

  /** 기존 함수에서 objB[key] === null이어도 바뀌었으니 key를 return하도록 */
  static getDifferentOnSecondObjectAndNullAble = <T>(
    objA: T,
    objB: T
  ): ObjectDifference<T> => {
    const difference: ObjectDifference<T> = {};

    for (const key in objB) {
      if (
        Object.prototype.hasOwnProperty.call(objA, key) &&
        Object.prototype.hasOwnProperty.call(objB, key) &&
        objB[key] !== objA[key]
      ) {
        if (Array.isArray(objA[key]) && Array.isArray(objB[key])) {
          const isDifference = getArrayDifference(
            objA[key] as string[],
            objB[key] as string[]
          );

          isDifference && (difference[key] = objB[key]);
        } else if (
          typeof objA[key] === "object" &&
          typeof objB[key] === "object"
        ) {
          const nestedDifference = Util.getDifferentOnSecondObject(
            objA[key],
            objB[key]
          );

          if (Object.keys(nestedDifference).length > 0) {
            difference[key] = nestedDifference as T[keyof T];
          }
        } else {
          difference[key] = objB[key];
        }
      }
    }
    return difference;
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static removeExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf(".");

    // 확장자가 있는지 ? 있으면 확장자 제거 후 반환, 없다면 그대로 반환
    return lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
  };

  /**  특정 경로에서 첫 번째 / 이후의 부분을 추출
   * @author c_bae
   * 예시) aaa/bbb/ccc -> /bbb/ccc
   */
  static extractPathAfterFirstSlash = (path: string): string | null => {
    // 첫 번째 '/'의 위치를 찾습니다.
    const firstSlashIndex = path.indexOf("/");

    // 첫 번째 '/'가 존재하는지 확인합니다.
    if (firstSlashIndex === -1) {
      return null; // '/'가 없다면 null을 반환합니다.
    }

    // 첫 번째 '/' 이후의 문자열을 반환합니다.
    return path.substring(firstSlashIndex);
  };

  /**
   *
   * @param filename
   * @returns [파일 이름, 확장자]
   * @author c_bae
   */
  static getSplitFileNameAndExtension = (
    filename: string
  ): [string, string] => {
    const lastDotIndex = filename.lastIndexOf(".");

    if (lastDotIndex !== -1) {
      // 확장자가 있는 경우
      const name = filename.substring(0, lastDotIndex);
      const extension = filename.substring(lastDotIndex + 1);
      return [name, extension];
    } else {
      // 확장자가 없는 경우
      return [filename, ""];
    }
  };

  /**
   * @author
   * @param
   * @returns
   * @description
   */
  static hasExtension(fileName: string): boolean {
    // 파일 이름에서 마지막 점의 인덱스를 찾기
    const lastDotIndex = fileName.lastIndexOf(".");

    // 파일 이름에 점이 없으면 확장자가 없다고 판단
    if (lastDotIndex === -1) {
      return false;
    }

    // 파일 이름에 점이 있으면 확장자가 있다고 판단
    return lastDotIndex < fileName.length - 1;
  }

  static replaceExcapeCharacterToString = (excapeChar: string) => {
    const newText = excapeChar.replace(/\\(.)/g, (_, char) => {
      switch (char) {
        case "n":
          return "\n";
        case "t":
          return "\t";
        // 추가적인 이스케이프 문자가 있으면 여기에 계속 추가
        default:
          return char;
      }
    });
    return newText;
  };

  /**
   * @
   * @author c_bae
   * @description 이스케이프 시퀀스 문자 -> 일반 문자 ( 역으로도 가능 ) \n과 \t 만 적용
   * @param str
   * @param isReverse / true - 이스케이프 시퀀스 문자 -> 일반 문자 / false - 일반 문자 -> 이스케이프 시퀀스 문자
   */
  static changeEscapeSpecialToChar = (
    str: string,
    isReverse: boolean
  ): string => {
    // 이스케이프 시퀀스 문자 -> 일반 문자

    if (!str) {
      return "";
    }

    if (isReverse) {
      return str.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
    } else {
      return str.replace(/\n/g, "\\n").replace(/\t/g, "\\t");
    }
  };

  /**
   * @author c_bae
   * @description 공통 - 정규 표현식의 특수 문자를 이스케이프 처리
   * @param {string} value
   * @returns {string}
   */
  static escapeRegExp = (value: string): string => {
    return value?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") || value;
  };

  /**
   * @description 두 인자를 비교하여 일치하는지 판단 ( 순서는 고려하지 않기 때문에 정렬 후 사용해야함 )
   * @param arrA
   * @param arrB
   * @returns boolean
   * @author c_bae
   */
  static isEqualArray = (arrA: any[], arrB: any[]): boolean => {
    return (
      arrA.length === arrB.length &&
      arrA.every((value, index) => value === arrB[index])
    );
  };

  /**
   * @description 비어있는 객체인지 판단
   * @param obj
   * @returns boolean
   * @author c_bae
   */
  static isEmptyObject = (obj: any): boolean => {
    return Object.keys(obj).length === 0;
  };

  /**
   * @description 값이 비어있으면 Null로 변환
   * @param e.target.value 는 문자타입의 숫자
   * @author c_bae
   */
  static changeEmptyNumberToNull = (
    e: React.ChangeEvent<HTMLInputElement>,
    onValueChange: (
      changedOptionName: string,
      changedOptionValue: number | null
    ) => void
  ) => {
    const value = e.target.value;
    const name = e.target.name;

    if (!/^\d+$/.test(value) && value !== "") {
      return false;
    }

    const numericValue = Number(value);

    /** 최대값 지정 TODO: maxLength */
    if (numericValue > 100000000000) {
      return false;
    }

    if (!isNaN(numericValue)) {
      onValueChange(name, value === "" ? null : numericValue);
    }
  };
  static changeEmptyNumberToNull2 = (
    inputValue: string,
    inputName: string,
    onValueChange: (
      changedOptionName: string,
      changedOptionValue: number | null
    ) => void
  ) => {
    if (!/^\d+$/.test(inputValue) && inputValue !== "") {
      return false;
    }

    const numericValue = Number(inputValue);

    /** 최대값 지정 TODO: maxLength */
    if (numericValue > 100000000000) {
      return false;
    }

    if (!isNaN(numericValue)) {
      onValueChange(inputName, inputValue === "" ? null : numericValue);
    }
  };

  /**
   * @description 값이 비어있으면 Null로 변환
   * @param value 는 문자타입
   * @author c_bae
   */
  static changeEmptyStringToNull = (value: string): string | null => {
    return value === "" ? null : value;
  };

  /**
   * FileFormat에 따른 exportName 변경 함수
   * @description fileFormat에 따라 확장자 추가, jdbc인 경우엔 exportFileFormat를 보내지 않음
   * @author c_bae
   */
  static settingExportNameByFileFormat = (
    oldExportName: string,
    exportFileFormat?: LibraryFileFormatKeyType
  ): string => {
    let newExportName = oldExportName;

    /** 확장자가 있다면 일단 확장자 제거 */
    if (Util.hasExtension(oldExportName)) {
      newExportName = Util.removeExtension(oldExportName);
    }

    if (exportFileFormat) {
      const formatOptionsToFileExt: {
        [key in LibraryFileFormatKeyType]: string;
      } = {
        Avro: ".avro",
        Delimited: ".csv",
        Excel: ".xlsx",
        Fixed: ".txt",
        Json: ".json",
        Xml: ".xml",
      };

      newExportName += formatOptionsToFileExt[exportFileFormat];
    }
    return newExportName;
  };
  /**
   * @author c_bae
   */
  static isMariaOrMysql = (
    dbType: ConnectorDbValueType | undefined
  ): boolean => {
    return dbType === "MariaDB" || dbType === "Mysql";
  };

  /**
   * @description 고객 요청으로 Oracle도 스키마부터 검색되도록 대신 databases는 USERS로 하드코딩
   * @author c_bae
   */
  static isOracle = (dbType: ConnectorDbValueType | undefined): boolean => {
    return dbType === "Oracle";
  };

  /**
   * @description DB Type Tibero 확인
   * @author hc_yang
   */
  static isTibero = (dbType: ConnectorDbValueType | undefined): boolean => {
    return dbType === "Tibero";
  };

  /**
   * @author hc_yang
   * @param data 암호화 할 문자열 데이터
   * @returns 암호화 된 문자열 데이터를 반환
   * @todo 암호화 키인 "$$SYNCBOX_ENCRYPTION_KEY" 는 추후 환경변수로 변경 예정
   */
  static encryptData = (data: string): string => {
    const encryptedData = CryptoJS.AES.encrypt(
      data,
      "$$SYNCBOX_ENCRYPTION_KEY" // 추후 환경변수로 변경 예정
    ).toString();
    return encryptedData;
  };

  /**
   * @author hc_yang
   * @param encryptedData 복호화 할 문자열 데이터
   * @returns 복호화 된 문자열 데이터를 반환
   * @todo 암호화 키인 "$$SYNCBOX_ENCRYPTION_KEY" 는 추후 환경변수로 변경 예정
   */
  static decryptData = (encryptedData: string): string => {
    const decryptedBytes = CryptoJS.AES.decrypt(
      encryptedData,
      "$$SYNCBOX_ENCRYPTION_KEY" // 추후 환경변수로 변경 예정
    );
    const decryptedData = decryptedBytes.toString(CryptoJS.enc.Utf8);
    return decryptedData;
  };

  /**
   * @author hc_yang
   * @param obj1 비교할 대상의 객체 1
   * @param obj2 비교할 대상의 객체 2
   * @returns booelan
   * @description
   *    - obj1, obj2가 서로 완전히 동일하다면 false, 서로 다르다면 true 리턴.
   *    - api 데이터 전송시, 기존 전송한 데이터와 새로 보내려는 데이터의
   *    - 값이 일치하는지 확인하는 함수이며, 같은 데이터를 중복으로 전송하지 않도록
   *    - 확인하기 위한 용도의 함수입니다.
   */
  static checkObjectEquality = <T extends object>(
    obj1: T,
    obj2: T
  ): boolean => {
    const keys1 = Object.keys(obj1) as (keyof T)[];
    const keys2 = Object.keys(obj2) as (keyof T)[];

    // 키의 수가 다르면 두 객체는 다르다고 판단
    if (keys1.length !== keys2.length) return true;

    // 각 키의 값이 동일한지 확인
    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) return true;
    }
    // 모든 키와 값이 동일하다면 두 객체는 동일하다고 판단
    return false;
  };

  static toggleSort = (sort: ColumnSortType, action?: any) => {
    if (action && action.type === "reset") {
      return false;
    }
    if (action && action.type === "set") {
      return action.value;
    } else {
      return sort === "asc" ? "desc" : "asc";
    }
  };

  static getRandomKey = (size: number) => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";
    for (let i = 0; i < size; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      key += characters.charAt(randomIndex);
    }
    return key;
  };

  static getRandomColorFromUUID = (str: string) => {
    let hash = 0;
    if (str.length === 0) {
      return hash;
    }

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }
    const index = Math.abs(hash) % DATASET_COLORSET.length; // 모듈러스 연산을 통해 인덱스 결정
    return DATASET_COLORSET[index];
  };

  static checkStringForWhitespace = (inputString: string) => {
    if (!inputString) return null;
    if ((inputString + "").trim() === "" && !inputString.includes(" "))
      return null;
    else return inputString;
  };

  static getRelativePathForExport = (pathArr: string[]) => {
    try {
      if (pathArr.length === 0) {
        throw new Error("배열의 길이가 0입니다.");
      }

      const relativePath = pathArr.reduce((acc, cur, idx) => {
        if (cur === "") return acc;

        return idx === 0 ? "" : acc + "/" + cur;
      }, "");

      return relativePath;
    } catch (e) {
      console.error(e);
    }
  };

  static getRelativePath = (pathArr: string[]) => {
    if (pathArr.length === 0) {
      return "/";
    }

    // 첫 번째 요소를 제외한 나머지 요소들을 슬래시로 결합
    const relativePath = pathArr.slice(1).join("/");

    // 슬래시를 앞에 붙여서 반환
    return `/${relativePath}`;
  };

  static removeDuplicateSlash = (path: string) => {
    return path.replace(/\/+/g, "/");
  };

  /**
   * @description  파일 시스템에서 디렉토리 및 파일을 검색할 때 사용하는 글로브(glob) 패턴인지 확인하는 함수 for Import Sftp Wildcard
   * @param path 경로 string 전달
   * @returns 글로브 패턴이면 true, 아니면 false 반환
   * @author c_bae
   */
  static isGlobPattern = (path: string): boolean => {
    // 글로브 패턴에 사용되는 특수 문자
    const globChars = [
      "*",
      "?",
      "[",
      "]",
      "{",
      "}",
      "(",
      ")",
      "!",
      "+",
      "@",
      "|",
      "\\",
    ];

    // path 문자열에 글로브 특수 문자가 있는지 확인
    for (const char of globChars) {
      if (path.includes(char)) {
        return true;
      }
    }

    return false;

    // 테스트 예제
    // const testPaths = [
    //   "src/**/*.js",
    //   "src/main.ts",
    //   "documents/*.pdf",
    //   "/home/user/*",
    //   "/home/user/docs",
    //   "images/2021/???/image.png",
    // ];

    // testPaths.forEach((path) => {
    //   console.log(
    //     `${path} is a ${
    //       Util.isGlobPattern(path) ? "glob pattern" : "regular path"
    //     }`
    //   );
    // });
  };

  static handleObjectClose = (
    objRef: React.MutableRefObject<any>,
    handleObjRefClose: (value: boolean) => void,
    type?: ObjectCloseType
  ) => {
    if (type?.click) {
      handleCloseOutsideClick(objRef, handleObjRefClose);
    } else {
      handleCloseOutsideMousedown(objRef, handleObjRefClose);
    }
    handleCloseESCKeyPress(objRef, handleObjRefClose);

    if (type?.wheelBlock) {
      handleOutsideWheelBlock(objRef);
    }
    if (type?.resize) {
      handleCloseResize(handleObjRefClose);
    }
  };

  static handleSortDataGridList = async function <
    ListType extends AdminListType | DatasourceListType,
  >(
    sortColumn: ColumnNameType,
    sortOrder: ColumnSortType,
    onListChange: (newList: ListType[]) => void,
    list: ListType[]
  ): Promise<void> {
    const sortedList = list.slice().sort((a: ListType, b: ListType) => {
      const valueA = a[sortColumn];
      const valueB = b[sortColumn];

      if (typeof valueA === "string" && typeof valueB === "string") {
        if (sortOrder === "desc") {
          return valueB.localeCompare(valueA); // 내림차순: B가 A보다 크면 양수 -> B가 앞으로
        } else {
          return valueA.localeCompare(valueB); // 오름차순: A가 B보다 크면 음수 -> A가 앞으로
        }
      } else if (typeof valueA === "number" && typeof valueB === "number") {
        if (sortOrder === "desc") {
          // 내림차순: 큰 수가 앞으로
          return valueB - valueA;
        } else {
          // 오름차순: 작은 수가 앞으로
          return valueA - valueB;
        }
      } else if (!valueA || !valueB) {
        // null/undefined 값은 정렬 순서와 관계없이 항상 뒤로
        return !valueA ? 1 : -1;
      } else {
        return 1;
      }
    });

    onListChange(sortedList);
  };

  /**
   * @description 공백 같은 문자들을 제거하기 위한
   * @param string 공백 같은 문자들 제거하기 위한 string 전달
   * @returns 공백과 같은 문자들을 모두 제거한 string 반환
   */
  static removeAllWhiteSpace = (str: string) => {
    // \s는 공백 문자(스페이스, 탭, 줄바꿈 등)에 매칭됩니다.
    // \x00-\x1F는 ASCII 제어 문자 범위입니다. \x7F는 DEL 제어 문자입니다.
    return str.replace(/[\s\x00-\x1F\x7F]/g, "");
  };

  /**
   * @description 공백은 포함하며, 줄바꿈 (\r\n\t)는 input안에 안들어가도록
   * @param string
   * @returns '공백'을 제외한 줄바꿈 같은 문자들을 모두 제거한 string 반환
   */
  static removeWhiteSpaceExcludeSpaceBar = (str: string) => {
    return str.replace(/[\r\n\t\x00-\x1F\x7F]/g, "");
  };

  /**
   * @description 첫글자만 대문자로 바꿔주는 함수
   * @param string 영어 문자열
   * @returns 첫글자만 대문자로 바뀐 문자열
   */
  static capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  static convertColumnName = (
    columnName: string,
    preprocessMode: StepCommandType
  ): string => {
    if (!columnName.includes("$$_ORIGIN_")) {
      if (preprocessMode === "replace") {
        return `$$_ORIGIN_REPLACE_${columnName}`;
      }
      if (preprocessMode === "fill") {
        return `$$_ORIGIN_FILL_${columnName}`;
      }
      return columnName;
    }
    return columnName;
  };

  /**
   * @description 쉼표를 포함한 문자열을 숫자로 변환하는 함수
   * @author c_bae
   * @param str 쉼표를 포함한 문자열
   */
  static convertStringWithCommasToNumber = (str: string) => {
    return Number(str.replace(/,/g, ""));
  };

  static isEqual = (
    obj1: object,
    obj2: object,
    excludeKeyList?: string[]
  ): boolean => {
    if (excludeKeyList) {
      return _.isEqual(
        _.omit(obj1, excludeKeyList),
        _.omit(obj2, excludeKeyList)
      );
    } else {
      return _.isEqual(obj1, obj2);
    }
  };

  /**
   * @param obj1 비교 객체 1
   * @param obj2 비교 객체 2
   * @description
   *  - 개발용 함수, 이전 스텝과 새로운 스텝을 서로 비교해서
   *  - 서로 다르다면 어떤 속성이 다른건지 찾아내는 함수
   */
  static findDifferentProperties = (
    obj1: IndexObjectType,
    obj2: IndexObjectType
  ) => {
    // 두 객체가 서로 다르다면 여기에 도달합니다.
    // 차이를 찾기 위해 다음과 같이 할 수 있습니다.
    const obj1Keys = Object.keys(obj1);
    const obj2Keys = Object.keys(obj2);

    // 두 객체의 키 목록을 결합하여 모든 키를 확인합니다.
    const allKeys = _.union(obj1Keys, obj2Keys);

    // 두 객체 사이의 차이를 확인합니다.
    const differentKeys = allKeys.filter((key) => {
      // 두 객체 중 하나에 키가 없으면 차이가 있음을 나타냅니다.
      if (!obj1.hasOwnProperty(key) || !obj2.hasOwnProperty(key)) {
        return true;
      }

      // 두 객체의 값이 다르면 차이가 있음을 나타냅니다.
      return !_.isEqual(obj1[key], obj2[key]);
    });

    return differentKeys;
  };

  /**
   * @author c_bae
   * @description data에서 unnecessaryKeys를 제거한 데이터를 반환
   * @param data
   * @param unnecessaryKeys
   * @returns filteredData
   */
  static removeUnnecessaryData = (
    data: { [key: string]: any },
    unnecessaryKeys: string[]
  ) => {
    const filteredData = { ...data };
    unnecessaryKeys.forEach((key) => {
      if (key in filteredData) {
        delete filteredData[key];
      }
    });
    return filteredData;
  };

  // 소켓 상태를 확인하는 함수
  static checkSocketState = (socket: WebSocket): string => {
    switch (socket.readyState) {
      case WebSocket.CONNECTING:
        return "CONNECTING";
      case WebSocket.OPEN:
        return "OPEN";
      case WebSocket.CLOSING:
        return "CLOSING";
      case WebSocket.CLOSED:
        return "CLOSED";
      default:
        return "UNKNOWN";
    }
  };

  /**
   * @description URL에 포함되는 문자열을 인코딩 처리하는 함수
   * @author c_bae
   * @param query
   * @returns encodedQuery
   */
  static encodeAndNormalize = (query: string): string => {
    const normalizedQuery = Util.normalizeSearch(query); // 정규화 적용

    if (normalizedQuery === "") return "";

    const encodedQuery = encodeURIComponent(normalizedQuery);
    return encodedQuery;
  };

  static normalizeSearch = (query: string | null): string => {
    return query ? query.normalize("NFC") : "";
  };

  /**
   * @description isValid - true : 통과, false: 실패
   * @param columnName
   * @returns ValidResponseType
   */

  static validateColumnName = (columnName: string): ValidResponseType => {
    // Check if the column name is a reserved word
    if (COLUMN_NAME_RESERVED_WORDS.includes(columnName.toUpperCase())) {
      return {
        isValid: false,
        errorMessage: "The column name is a reserved word.",
      };
    }

    // Check if the column name starts with a number
    // if (/^[0-9]/.test(columnName)) {
    //   return {
    //     isValid: false,
    //     errorMessage: "The column name cannot start with a number.",
    //   };
    // }

    // Check if the column name contains invalid backticks
    if (/[`]/.test(columnName)) {
      return {
        isValid: false,
        errorMessage: `The column name cannot with backticks.`,
      };
    }

    // Check the length of the column name (taking the most restrictive limit)
    // if (columnName.length > 30) {
    //   // PostgreSQL limit for example
    //   return {
    //     isValid: false,
    //     errorMessage: `The column name is too long.\nThe maximum length is 30 characters.`,
    //   };
    // }

    return { isValid: true };
  };

  static createUUID = (): string => {
    return uuidv4();
  };

  static animateValue = (
    start: number,
    end: number,
    callback: (value: number) => void
  ) => {
    const stepDuration = 10; // 숫자 증가 간격 (밀리초)
    let current = start;

    const animate = () => {
      const step = (end - current) / stepDuration; // 변화량을 10등분해서 부드럽게 변환
      current += step;

      if (Math.abs(end - current) < 1) {
        callback(end); // 최종 값으로 설정
        return;
      }

      callback(Math.floor(current));
      requestAnimationFrame(animate); // 다음 프레임에 다시 호출
    };

    animate();
  };

  static delay = async (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  static calculateDecrement = (value: string | number): number => {
    const decimalLength = value.toString().split(".")[1]?.length || 0; // 소수점 이하 자릿수 계산

    // 소수점 이하 자릿수에 맞는 값을 계산하고 고정
    return parseFloat(
      (1 / Math.pow(10, decimalLength + 1)).toFixed(decimalLength + 1)
    );
  };

  /**
   * @author hc_yang
   * @param value css 단위 값
   * @returns 변환된 px 숫자값, 변환 불가능 할 경우 0 리턴
   * @description
   * - string 형식의 css 단위 값을 px 숫자 값으로 변환하는 함수
   * - ex) 20px => 20, 10rem => 100
   */
  static convertCssUnitToPx = (value: string): number => {
    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );

    if (value.endsWith("px")) {
      return parseFloat(value);
    }
    if (value.endsWith("rem")) {
      return parseFloat(value) * rootFontSize;
    } else if (value.endsWith("em")) {
      return parseFloat(value) * rootFontSize;
    } else if (value.endsWith("vw")) {
      return (parseFloat(value) * window.innerWidth) / 100;
    } else if (value.endsWith("vh")) {
      return (parseFloat(value) * window.innerHeight) / 100;
    } else if (value.endsWith("%")) {
      return 0;
    } else if (value.endsWith("cm")) {
      return parseFloat(value) * 37.795275591;
    } else if (value.endsWith("mm")) {
      return parseFloat(value) * 3.7795275591;
    } else if (value.endsWith("in")) {
      return parseFloat(value) * 96;
    } else if (value.endsWith("pt")) {
      return (parseFloat(value) * 96) / 72;
    } else if (value.endsWith("pc")) {
      return parseFloat(value) * 16;
    }

    // 변환 불가능한 경우 (예: max-content, auto 등)
    return 0;
  };

  /**
   * @author c_bae
   * @description 두 배열을 합쳐서 Set객체를 활용하여 중복을 제거한 배열을 반환합니다.
   * @param {T} arr1
   * @param {T} arr2
   * @returns
   */
  static removeDuplicatesFromArrays: <T>(arr1: T[], arr2: T[]) => T[] = (
    arr1,
    arr2
  ) => {
    return [...new Set([...arr1, ...arr2])];
  };

  /**
   * @author hc_yang
   * @description 문자열의 케이스를 변경하는 함수
   * @param
   */
  static convertCase = (
    str: string,
    caseType:
      | "camel"
      | "pascal"
      | "snake"
      | "upper_snake"
      | "kebab"
      | "upper_kebab"
      | "dot"
      | "train"
  ) => {
    const words = str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .split(/[^a-zA-Z0-9]/)
      .filter(Boolean);

    const lowerWords = words.map((word) => word.toLowerCase());

    switch (caseType) {
      case "camel":
        return lowerWords
          .map((word, index) =>
            index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
          )
          .join("");

      case "pascal":
        return lowerWords
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join("");

      case "snake":
        return lowerWords.join("_");

      case "upper_snake":
        return lowerWords.join("_").toUpperCase();

      case "kebab":
        return lowerWords.join("-");

      case "upper_kebab":
        return lowerWords.join("-").toUpperCase();

      case "dot":
        return lowerWords.join(".");

      case "train":
        return lowerWords
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join("-");

      default:
        throw new Error(`Unsupported case type: ${caseType}`);
    }
  };

  static convertLocaleString = (
    value: number,
    decimalPlaces?: number
  ): string => {
    return value === Infinity || value === -Infinity
      ? ""
      : value.toLocaleString("ko-KR", {
          maximumFractionDigits: decimalPlaces,
        });
  };
}

export const convertToTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const handleCloseOutsideClick = (
  objRef: React.MutableRefObject<any>,
  handleObjRefClose: (value: boolean) => void
) => {
  const onClickOutsideCheck = (event: MouseEvent) => {
    if (objRef.current && !objRef.current.contains(event.target as Node)) {
      handleObjRefClose(false);
    }
  };

  document.addEventListener("click", onClickOutsideCheck);

  return () => {
    document.removeEventListener("click", onClickOutsideCheck);
  };
};

export const handleCloseOutsideMousedown = (
  objRef: React.MutableRefObject<any>,
  handleObjRefClose: (value: boolean) => void
) => {
  const onClickOutsideCheck = (event: MouseEvent) => {
    if (objRef.current && !objRef.current.contains(event.target as Node)) {
      handleObjRefClose(false);
    }
  };

  document.addEventListener("mousedown", onClickOutsideCheck);

  return () => {
    document.removeEventListener("mousedown", onClickOutsideCheck);
  };
};

export const handleCloseESCKeyPress = (
  objRef: React.MutableRefObject<any>,
  handleObjRefClose: (value: boolean) => void
) => {
  const onPressESCKeyCheck = (event: KeyboardEvent) => {
    Util.handleKeyPress(event, "Escape", () => handleObjRefClose(false));
  };

  document.addEventListener("keydown", onPressESCKeyCheck);

  return () => {
    document.removeEventListener("keydown", onPressESCKeyCheck);
  };
};

export const handleOutsideWheelBlock = (
  objRef: React.MutableRefObject<any>
) => {
  const onWheelOutsideCheck = (event: WheelEvent) => {
    if (objRef.current && !objRef.current.contains(event.target as Node)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  document.addEventListener("wheel", onWheelOutsideCheck, { passive: false });

  return () => {
    document.removeEventListener("wheel", onWheelOutsideCheck);
  };
};

export const handleCloseResize = (
  handleObjRefClose: (value: boolean) => void
) => {
  const onResizeCheck = (event: UIEvent) => {
    handleObjRefClose(false);
  };

  document.addEventListener("resize", onResizeCheck);

  return () => {
    document.removeEventListener("resize", onResizeCheck);
  };
};

/** getDifferentOnSecondObject 에서 사용할 배열 비교 함수
 * @returns boolean - 배열이 서로 같지 않으면 true, 같은 배열이면 false
 * @description 1. 순서가 다르나 배열 안의 요소는 동일할 경우에도 같지 않다고 판단
 * @author c_bae
 * */
export const getArrayDifference = (arrA: string[], arrB: string[]): boolean => {
  for (let i = 0; i < Math.max(arrA.length, arrB.length); i++) {
    const valueA = arrA[i];
    const valueB = arrB[i];

    if (valueA !== valueB) {
      return true;
    }
  }
  return false;
};

/**
 * @author hc_yang
 * @param log 콘솔에 출력할 값
 * @param error 콘솔에 출력할 에러 값
 * @description
 *   - 하나의 콘솔에 여러개의 데이터를 찍을 경우에는 배열에 감싸서 전송
 *   - 개발 모드 중에만 출력하는 콘솔로그 함수입니다.
 *   - 기능 개발 중에 콘솔을 찍어보는 용도보다는,
 *   - api 호출 결과 등과 같이 개발 중에만 고정적으로 값을 표출하는 용도입니다.
 *   - 되도록 텍스트만 출력할 것을 권장합니다.
 * @example
 *   - ex) devLog(["1", 2, [3]])
 *   - ==> console => "1", 2, [3]
 */
export const devLog = (log?: any, error?: any): void => {
  if (DEV_MODE) {
    if (Array.isArray(log)) console.log(...log);
    else console.log(log);

    if (error) {
      if (Array.isArray(error)) console.error(...error);
      else console.error(error);
    }
  } else return;
};

export const devErrLog = (error?: any): void => {
  if (DEV_MODE) {
    if (Array.isArray(error)) console.error(...error);
    else console.error(error);
  } else return;
};

/**
 * @description TODO: depth 고려 필요
 * @author c_bae
 * @param object1
 * @param object2
 * @returns
 */
export const getDifferences = (
  object1: { [key in string]: any },
  object2: { [key in string]: any }
) => {
  let differences: { [key in string]: any } = {};

  _.forEach(object2, (value, key) => {
    if (_.isObject(value)) {
      if (!_.isEqual(value, object1[key])) {
        differences[key] =
          _.isObject(object1[key]) && _.isObject(value)
            ? getDifferences(object1[key], value)
            : value;
      }
    } else {
      if (!_.isEqual(value, object1[key])) {
        differences[key] = value;
      }
    }
  });

  return differences;
};

// 리스트 초기화 시 스크롤을 제일 위로 옮겨 주는 함수
export const scrollToTop = () => {
  const scrollContainer = document.querySelector(".virtualized-container");
  if (scrollContainer) scrollContainer.scrollTo({ top: 0 });
};

/**
 * @description APF, 버전, 런 리스트 검색 시 입력 받는 object를 요청 parameter 형태로 변환시키는 함수
 * @author js_baek
 * @param form 검색 시 입력받는 값들
 * @param isRunList run list의 경우 search를 보내지 않아야 함
 * @returns
 */
export const setApfSearchParams = (
  form: GetProjectFlowListForm,
  isRunList?: boolean
) => {
  if (!form) return undefined;
  const searchParam: ApfSearchParameter = { rows: form.rows, page: form.page };

  if (form.status.length > 0) {
    let statusString = "";
    form.status.forEach((status, index) => {
      statusString += `${index === 0 ? "" : ","}${status.value}`;
    });
    searchParam.status = statusString;
  }

  if (form.search && !isRunList)
    searchParam.search = Util.encodeAndNormalize(form.search);
  if (form.nextRunDate?.startDate && form.nextRunDate.endDate)
    searchParam.nextRunDate = `${form.nextRunDate.startDate},${form.nextRunDate.endDate}`;
  if (form.lastFinishedDate?.startDate && form.lastFinishedDate.endDate)
    searchParam.lastFinishedDate = `${form.lastFinishedDate.startDate},${form.lastFinishedDate.endDate}`;
  if (form.sortColumn && form.sortOrder) {
    searchParam.sortColumn = form.sortColumn;
    searchParam.sortOrder = form.sortOrder;
  }
  if (form.typeSearch) {
    searchParam.typeSearch = form.typeSearch;
  }
  if (form.apfGroupIdSearch) {
    searchParam.apfGroupIdSearch = form.apfGroupIdSearch;
  }

  return searchParam;
};

/**
 * @description 초 단위의 시간을 입력받아 분, 초 단위로 리턴해주는 함수
 * @author js_baek
 * @param {number} time 컨버팅할 시간
 */
export const returnDurationTime = (time: number) => {
  if (!time) return "< 1 second"; // 0.~~초일 때나 오류로 시간이 들어오지 않았을 경우...
  if (time >= 60) {
    const minutesValue = Math.floor(time / 60);
    const secondsValue = time % 60 ? time % 60 : "";
    return `${minutesValue} minute${minutesValue > 1 ? "s" : ""} ${
      secondsValue
        ? secondsValue + (secondsValue > 1 ? " seconds" : " second")
        : ""
    }`;
  } else {
    return `${time} second${time > 1 ? "s" : ""}`;
  }
};

/**
 * @description  대상 리스트, 기준이 되는 값, 키값을 넣으면 원하는 조건의 값이 위치하는 인덱스를 리턴
 * @author js_baek
 * @param {any[]} list 탐색 대상 리스트
 * @param {string} target 판단의 기준이 되는 값
 * @param {string} key 판단을 위한 키값
 */
export const getTargetIndex = (
  list: any[],
  target: string | number,
  key: string
) => {
  if (!list || list.length === 0) return -1;

  const targetIndex = list.findIndex((item) => item[key] === target);

  return targetIndex;
};

export const isConvertibleToNumber = (input: string): boolean => {
  const converted = Number(input);
  return !isNaN(converted); // 변환 가능 여부 확인
};

/**
 * @description 세로 스크롤 발생한 것을 가로 스크롤 이동으로 바꾸어주는 함수
 * @param e wheel event 객체
 * @param target 대상의 ref
 */
export const wheelConvert = (e: React.WheelEvent, target: any) => {
  const { deltaX, deltaY } = e;
  if (target.current) {
    target.current.scrollLeft += deltaY + deltaX;
  }
};

export const getAutoUpdateData = () => {
  const localStorageItem = localStorage.getItem("autoUpdateData");
  const autoUpdateData = localStorageItem ? JSON.parse(localStorageItem) : {};

  return autoUpdateData;
};

export const getFileFormatByExtension = (
  extension: string
): LibraryFileFormatKeyType => {
  switch (extension) {
    case "csv":
      return "Delimited";
    case "txt":
      return "Fixed";
    case "json":
      return "Json";
    case "xml":
      return "Xml";
    case "avro":
      return "Avro";
    case "xlsx":
      return "Excel";

    default:
      return "Delimited";
  }
};

export const getFileFormatInitDto = (fileFormat: LibraryFileFormatKeyType) => {
  return {
    ...(fileFormat === "Delimited" && {
      delimited: FILE_FORMAT_OPTION_INIT_DTO_EXPORT.delimited,
    }),
    ...(fileFormat === "Fixed" && {
      fixed: FILE_FORMAT_OPTION_INIT_DTO_EXPORT.fixed,
    }),
    ...(fileFormat === "Excel" && {
      excel: FILE_FORMAT_OPTION_INIT_DTO_EXPORT.excel,
    }),
    ...(fileFormat === "Avro" && {
      avro: FILE_FORMAT_OPTION_INIT_DTO_EXPORT.avro,
    }),
    ...(fileFormat === "Json" && {
      json: FILE_FORMAT_OPTION_INIT_DTO_EXPORT.json,
    }),
    ...(fileFormat === "Xml" && {
      xml: FILE_FORMAT_OPTION_INIT_DTO_EXPORT.xml,
    }),
  };
};
